/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { TooltipSelect } from '@opengeoweb/shared';
import type { CustomDimension, Parameter } from '../../TimeSeries/types';

const defaultValue = 'none';

const ParameterDimensionValueSelect: React.FC<{
  rowIsEnabled: boolean;
  parameter: Parameter;
  customDimensions: CustomDimension[];
  patchParameter: (parameter: Partial<Parameter>) => void;
  t: TFunction;
}> = ({ rowIsEnabled, parameter, customDimensions, patchParameter, t }) => {
  const selectedDimension =
    customDimensions.find(
      (dimension) => dimension.id === parameter.dimension?.id,
    ) || customDimensions[0];

  if (
    !selectedDimension ||
    !customDimensions ||
    customDimensions.length === 0
  ) {
    return null;
  }

  return (
    <TooltipSelect
      SelectDisplayProps={{ 'aria-label': 'timeseriesManager-dimensionValue' }}
      data-testid="parameterDimensionValueSelect"
      isEnabled={rowIsEnabled}
      onChange={(event: SelectChangeEvent<unknown>): void => {
        const newParameter = {
          id: parameter.id,
          dimension: {
            id: parameter.dimension?.id || customDimensions[0].id,
            value: event.target.value as string,
          },
        };
        patchParameter(newParameter);
      }}
      tooltip={`${t('timeseries-dimension-value')}: ${parameter.dimension?.value !== undefined ? parameter.dimension?.value : ''}`}
      value={
        parameter.dimension?.value === undefined ||
        parameter.dimension?.value === ''
          ? defaultValue
          : parameter.dimension?.value
      }
    >
      <MenuItem disabled>{t('timeseries-dimension-value')}</MenuItem>
      {(parameter.dimension?.value === undefined ||
        parameter.dimension?.value === '') && (
        <MenuItem
          data-testid="timeseries-dimension-value-none"
          key="dimensionvaluemenu-none"
          value="none"
        >
          {t('timeseries-dimension-none')}
        </MenuItem>
      )}
      {selectedDimension?.values?.map((value) => {
        return (
          <MenuItem
            data-testid={`timeseries-dimension-value-${value}`}
            key={`dimensionvaluemenu-${value}`}
            value={value}
          >
            {value}
          </MenuItem>
        );
      })}
    </TooltipSelect>
  );
};

export default ParameterDimensionValueSelect;
